import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '@/i18n/I18nProvider';
import { medirArea, medirDistancia } from '@/map/calculosMedicao';
import { useMapa } from '@/map/Mapa';
import { TIPO_GEOJSON, baixarArquivo, construirGeoJsonDeMedicao, nomeDoGeoJsonDeMedicao, serializarGeoJson, } from '@/utils/exportacao';
const COLECAO_VAZIA = { type: 'FeatureCollection', features: [] };
const COR_DA_MEDICAO = '#d9480f';
/**
 * O `click` do MapLibre só é emitido se o ponteiro se mover até `clickTolerance` (3 px) entre o
 * botão pressionado e solto; acima disso o gesto vira arrasto e o mapa se move sem vértice algum.
 * Um mouse ou trackpad comum já excede 3 px num clique, então o toque é reconhecido aqui, com
 * tolerância maior, a partir dos eventos `mousedown`/`mouseup` do próprio MapLibre.
 */
const TOLERANCIA_DO_TOQUE_PX = 8;
/** Janela em que o `click` que acompanha um toque já tratado é ignorado (sem vértice duplicado). */
const JANELA_DO_CLICK_DUPLICADO_MS = 500;
const ID_DA_FONTE_PROVISORIA = 'medicao:provisoria';
const ID_DA_FONTE_CONCLUIDA = 'medicao:concluida';
const FONTES = [ID_DA_FONTE_PROVISORIA, ID_DA_FONTE_CONCLUIDA];
const TIPOS_DE_LAYER = ['preenchimento', 'segmento', 'previa', 'vertice'];
const idDaLayer = (fonte, tipo) => `${fonte}:${tipo}`;
const CHAVE_DO_ERRO = {
    coordenadas_invalidas: 'medicao.erro.coordenadasInvalidas',
    minimo_dois_vertices: 'medicao.erro.minimoDoisVertices',
    minimo_tres_vertices: 'medicao.erro.minimoTresVertices',
    vertices_repetidos: 'medicao.erro.verticesRepetidos',
    auto_intersecao: 'medicao.erro.autoIntersecao',
    poligono_degenerado: 'medicao.erro.poligonoDegenerado',
};
const ehTipo = (tipo) => ['==', ['get', 'tipo'], tipo];
function feicao(geometria, tipo) {
    return { type: 'Feature', geometry: geometria, properties: { tipo } };
}
function verticesComo(vertices) {
    return vertices.map((v) => feicao({ type: 'Point', coordinates: [...v] }, 'vertice'));
}
/** Desenho em andamento: vértices, segmentos confirmados, preenchimento e prévia até o cursor. */
function colecaoProvisoria(modo, vertices, cursor) {
    if (modo === 'nenhum') {
        return COLECAO_VAZIA;
    }
    const features = verticesComo(vertices);
    if (vertices.length >= 2) {
        features.push(feicao({ type: 'LineString', coordinates: vertices.map((v) => [...v]) }, 'segmento'));
    }
    if (modo === 'area' && vertices.length >= 3) {
        features.push(feicao({ type: 'Polygon', coordinates: [[...vertices, vertices[0]].map((v) => [...v])] }, 'preenchimento'));
    }
    const ultimo = vertices.at(-1);
    if (cursor !== null && ultimo !== undefined) {
        const trajeto = modo === 'area' && vertices.length >= 2 ? [ultimo, cursor, vertices[0]] : [ultimo, cursor];
        features.push(feicao({ type: 'LineString', coordinates: trajeto.map((v) => [...v]) }, 'previa'));
    }
    return { type: 'FeatureCollection', features };
}
function colecaoConcluida(medicao) {
    if (medicao === null) {
        return COLECAO_VAZIA;
    }
    const { modo, vertices } = medicao;
    const features = verticesComo(vertices);
    if (modo === 'distancia') {
        features.push(feicao({ type: 'LineString', coordinates: vertices.map((v) => [...v]) }, 'segmento'));
    }
    else {
        const anel = [...vertices, vertices[0]].map((v) => [...v]);
        features.push(feicao({ type: 'Polygon', coordinates: [anel] }, 'preenchimento'));
        features.push(feicao({ type: 'LineString', coordinates: anel }, 'segmento'));
    }
    return { type: 'FeatureCollection', features };
}
/**
 * Ferramenta de medição cartográfica (distância e área). Estado local: desenho atual, cursor
 * provisório (ref, sem render por movimento) e a última medição concluída. Desenha em fontes
 * GeoJSON próprias `medicao:*`, no topo do estilo, sem tocar nas demais fontes e layers.
 * Resultados são estimativas esféricas auxiliares (ver `calculosMedicao.ts`).
 */
export function MedicaoCartografica({ modo, aoMudarModo }) {
    const mapa = useMapa();
    const [vertices, setVertices] = useState([]);
    const [concluida, setConcluida] = useState(null);
    const { t, formatar } = useI18n();
    const [mensagem, setMensagem] = useState(null);
    const modoRef = useRef(modo);
    modoRef.current = modo;
    const verticesRef = useRef(vertices);
    verticesRef.current = vertices;
    const cursorRef = useRef(null);
    const toqueRef = useRef(null);
    const ultimoToqueEmRef = useRef(0);
    // Fontes e layers próprias: criadas uma vez por instância do mapa e removidas ao desmontar.
    useEffect(() => {
        if (mapa === null) {
            return undefined;
        }
        // `Mapa` chama `instancia.remove()` na própria limpeza, que pode rodar antes da nossa; o
        // MapLibre destrói então o estilo interno e dispara `remove`. Depois disso, consultar ou
        // remover sources e layers lança `TypeError` (e derruba a árvore React).
        let destruido = false;
        const aoRemoverMapa = () => {
            destruido = true;
        };
        mapa.on('remove', aoRemoverMapa);
        for (const fonte of FONTES) {
            if (mapa.getSource(fonte) !== undefined) {
                continue;
            }
            const provisoria = fonte === ID_DA_FONTE_PROVISORIA;
            mapa.addSource(fonte, { type: 'geojson', data: COLECAO_VAZIA });
            mapa.addLayer({
                id: idDaLayer(fonte, 'preenchimento'),
                type: 'fill',
                source: fonte,
                filter: ehTipo('preenchimento'),
                paint: { 'fill-color': COR_DA_MEDICAO, 'fill-opacity': provisoria ? 0.14 : 0.22 },
            });
            mapa.addLayer({
                id: idDaLayer(fonte, 'segmento'),
                type: 'line',
                source: fonte,
                filter: ehTipo('segmento'),
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: { 'line-color': COR_DA_MEDICAO, 'line-width': 3 },
            });
            mapa.addLayer({
                id: idDaLayer(fonte, 'previa'),
                type: 'line',
                source: fonte,
                filter: ehTipo('previa'),
                paint: { 'line-color': COR_DA_MEDICAO, 'line-width': 2, 'line-dasharray': [2, 2] },
            });
            mapa.addLayer({
                id: idDaLayer(fonte, 'vertice'),
                type: 'circle',
                source: fonte,
                filter: ehTipo('vertice'),
                paint: {
                    'circle-radius': 5,
                    'circle-color': '#ffffff',
                    'circle-stroke-color': COR_DA_MEDICAO,
                    'circle-stroke-width': 2,
                },
            });
        }
        return () => {
            mapa.off('remove', aoRemoverMapa);
            // Mapa já destruído: o estilo não existe mais e levou consigo as sources e layers.
            if (destruido) {
                return;
            }
            for (const fonte of FONTES) {
                for (const tipo of TIPOS_DE_LAYER) {
                    if (mapa.getLayer(idDaLayer(fonte, tipo)) !== undefined) {
                        mapa.removeLayer(idDaLayer(fonte, tipo));
                    }
                }
                if (mapa.getSource(fonte) !== undefined) {
                    mapa.removeSource(fonte);
                }
            }
        };
    }, [mapa]);
    // Trocar ou iniciar um modo descarta o desenho anterior; a medição concluída é preservada.
    useEffect(() => {
        setVertices([]);
        setMensagem(null);
        cursorRef.current = null;
        toqueRef.current = null;
    }, [modo]);
    // Toques e movimento só valem com um modo ativo; o filtro está no próprio handler. Os handlers
    // são registrados uma única vez por mapa e leem o modo por ref, sem ficarem obsoletos.
    useEffect(() => {
        if (mapa === null) {
            return undefined;
        }
        const adicionarVertice = (lng, lat) => {
            setMensagem(null);
            setVertices((atuais) => [...atuais, [lng, lat]]);
        };
        const aoPressionar = (evento) => {
            const botao = evento.originalEvent?.button ?? 0;
            if (modoRef.current === 'nenhum' || botao !== 0) {
                toqueRef.current = null;
                return;
            }
            toqueRef.current = { x: evento.point.x, y: evento.point.y, ...evento.lngLat };
        };
        const aoSoltar = (evento) => {
            const inicio = toqueRef.current;
            toqueRef.current = null;
            if (inicio === null || modoRef.current === 'nenhum') {
                return;
            }
            // Arrasto de verdade: o mapa foi movido e nenhum vértice é marcado.
            if (Math.hypot(evento.point.x - inicio.x, evento.point.y - inicio.y) > TOLERANCIA_DO_TOQUE_PX) {
                return;
            }
            ultimoToqueEmRef.current = Date.now();
            adicionarVertice(inicio.lng, inicio.lat);
        };
        // Toques de tela e o clique estacionário ainda chegam como `click`.
        const aoClicar = (evento) => {
            if (modoRef.current === 'nenhum' ||
                Date.now() - ultimoToqueEmRef.current < JANELA_DO_CLICK_DUPLICADO_MS) {
                return;
            }
            adicionarVertice(evento.lngLat.lng, evento.lngLat.lat);
        };
        const aoMover = (evento) => {
            if (modoRef.current === 'nenhum') {
                return;
            }
            const { lng, lat } = evento.lngLat;
            cursorRef.current = [lng, lat];
            // Só a prévia é atualizada: nenhuma fonte ou layer é recriada e o React não renderiza.
            mapa
                .getSource(ID_DA_FONTE_PROVISORIA)
                ?.setData(colecaoProvisoria(modoRef.current, verticesRef.current, cursorRef.current));
        };
        mapa.on('mousedown', aoPressionar);
        mapa.on('mouseup', aoSoltar);
        mapa.on('click', aoClicar);
        mapa.on('mousemove', aoMover);
        return () => {
            mapa.off('mousedown', aoPressionar);
            mapa.off('mouseup', aoSoltar);
            mapa.off('click', aoClicar);
            mapa.off('mousemove', aoMover);
        };
    }, [mapa]);
    // Redesenha o provisório ao mudar vértices ou modo.
    useEffect(() => {
        mapa
            ?.getSource(ID_DA_FONTE_PROVISORIA)
            ?.setData(colecaoProvisoria(modo, vertices, cursorRef.current));
    }, [mapa, modo, vertices]);
    useEffect(() => {
        mapa?.getSource(ID_DA_FONTE_CONCLUIDA)?.setData(colecaoConcluida(concluida));
    }, [mapa, concluida]);
    // Cursor de mira só enquanto mede; a seleção de feições não o altera nesse período.
    useEffect(() => {
        if (mapa === null || modo === 'nenhum') {
            return undefined;
        }
        mapa.getCanvas().style.cursor = 'crosshair';
        return () => {
            mapa.getCanvas().style.cursor = '';
        };
    }, [mapa, modo]);
    const cancelar = () => {
        setVertices([]);
        setMensagem(null);
        cursorRef.current = null;
        aoMudarModo('nenhum');
    };
    const limpar = () => {
        setConcluida(null);
        cancelar();
    };
    // Escape equivale a Cancelar enquanto há desenho ativo.
    const cancelarRef = useRef(cancelar);
    cancelarRef.current = cancelar;
    useEffect(() => {
        if (modo === 'nenhum') {
            return undefined;
        }
        const aoPressionar = (evento) => {
            if (evento.key === 'Escape') {
                cancelarRef.current();
            }
        };
        document.addEventListener('keydown', aoPressionar);
        return () => document.removeEventListener('keydown', aoPressionar);
    }, [modo]);
    const provisorio = useMemo(() => {
        if (modo === 'distancia') {
            return vertices.length >= 2 ? medirDistancia(vertices) : null;
        }
        return modo === 'area' && vertices.length >= 3 ? medirArea(vertices) : null;
    }, [modo, vertices]);
    const concluir = () => {
        if (modo === 'nenhum') {
            return;
        }
        if (modo === 'distancia') {
            const medida = medirDistancia(vertices);
            if (!medida.ok) {
                setMensagem(medida.codigo);
                return;
            }
            setConcluida({ modo, vertices, distancia: medida.valor, area: null });
        }
        else {
            const medida = medirArea(vertices);
            if (!medida.ok) {
                setMensagem(medida.codigo);
                return;
            }
            setConcluida({ modo, vertices, distancia: null, area: medida.valor });
        }
        setVertices([]);
        setMensagem(null);
        cursorRef.current = null;
        aoMudarModo('nenhum');
    };
    if (modo !== 'nenhum') {
        const erroProvisorio = provisorio !== null && !provisorio.ok ? provisorio.codigo : null;
        const codigoDoErro = mensagem ?? erroProvisorio;
        return (_jsxs("section", { className: "medicao-cartografica", "aria-label": t('medicao.titulo'), children: [_jsx("h3", { className: "medicao-cartografica__titulo", children: modo === 'distancia' ? t('medicao.medirDistancia') : t('medicao.medirArea') }), _jsx("p", { className: "medicao-cartografica__texto", children: modo === 'distancia' ? t('medicao.instrucaoDistancia') : t('medicao.instrucaoArea') }), _jsx("p", { className: "medicao-cartografica__texto", children: t('medicao.vertices', { quantidade: vertices.length }) }), provisorio?.ok === true ? (_jsx("p", { className: "medicao-cartografica__resultado", role: "status", children: 'distanciaM' in provisorio.valor
                        ? t('medicao.distanciaParcial', { valor: formatar.comprimento(provisorio.valor.distanciaM) })
                        : t('medicao.areaParcial', {
                            area: formatar.area(provisorio.valor.areaM2),
                            perimetro: formatar.comprimento(provisorio.valor.perimetroM),
                        }) })) : null, codigoDoErro !== null ? (_jsx("p", { className: "medicao-cartografica__erro", role: "alert", children: t(CHAVE_DO_ERRO[codigoDoErro]) })) : null, _jsxs("div", { className: "medicao-cartografica__acoes", children: [_jsx("button", { type: "button", className: "medicao-cartografica__botao medicao-cartografica__botao--primario", onClick: concluir, disabled: provisorio?.ok !== true, children: t('medicao.concluir') }), _jsx("button", { type: "button", className: "medicao-cartografica__botao", onClick: cancelar, children: t('medicao.cancelar') }), _jsx("button", { type: "button", className: "medicao-cartografica__botao", onClick: limpar, children: t('medicao.limpar') })] })] }));
    }
    if (concluida === null) {
        return null;
    }
    // Exporta a medição concluída tal como guardada (valores internos, sem arredondamento); não
    // altera a medição, o modo nem a câmera.
    const exportarGeoJson = () => {
        const { distancia, area } = concluida;
        if (area !== null) {
            const documento = construirGeoJsonDeMedicao({ modo: 'area', vertices: concluida.vertices, ...area });
            baixarArquivo(serializarGeoJson(documento), nomeDoGeoJsonDeMedicao('area'), TIPO_GEOJSON);
        }
        else if (distancia !== null) {
            const documento = construirGeoJsonDeMedicao({
                modo: 'distancia',
                vertices: concluida.vertices,
                distanciaM: distancia.distanciaM,
            });
            baixarArquivo(serializarGeoJson(documento), nomeDoGeoJsonDeMedicao('distancia'), TIPO_GEOJSON);
        }
    };
    return (_jsxs("section", { className: "medicao-cartografica", "aria-label": t('medicao.resultadoAria'), children: [_jsx("h3", { className: "medicao-cartografica__titulo", children: t('medicao.tituloResultado') }), concluida.distancia !== null ? (_jsx("p", { className: "medicao-cartografica__resultado", children: t('medicao.distanciaTotal', { valor: formatar.comprimento(concluida.distancia.distanciaM) }) })) : null, concluida.area !== null ? (_jsxs(_Fragment, { children: [_jsx("p", { className: "medicao-cartografica__resultado", children: t('medicao.area', { valor: formatar.area(concluida.area.areaM2) }) }), _jsx("p", { className: "medicao-cartografica__resultado", children: t('medicao.perimetro', { valor: formatar.comprimento(concluida.area.perimetroM) }) })] })) : null, _jsx("p", { className: "medicao-cartografica__nota", children: t('medicao.nota') }), _jsxs("div", { className: "medicao-cartografica__acoes", children: [_jsx("button", { type: "button", className: "medicao-cartografica__botao medicao-cartografica__botao--exportar", onClick: exportarGeoJson, children: t('medicao.exportarGeoJson') }), _jsx("button", { type: "button", className: "medicao-cartografica__botao", onClick: limpar, children: t('medicao.limpar') })] })] }));
}
